"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";

import Header from "../../components/layout/header";
import Footer from "../../components/layout/footer";

import daycareHero from "../../assets/daycare/hero-1.jpg";
import daycare1 from "../../assets/daycare/activity-1.jpg";
import daycare2 from "../../assets/daycare/activity-2.jpg";
import daycare3 from "../../assets/daycare/activity-3.jpg";

const PRICING_DOCS = {
  external:
    "https://drive.google.com/file/d/1O3lK1eLTOQnLEW5VsyqA_1GPSXGD630_/view?usp=drive_link",
  internal:
    "https://drive.google.com/file/d/1MYMbZmpN2NOFqm95ybzuauaaDrKLEcm8/view?usp=drive_link",
};

// ubah /view → /preview untuk bisa di-embed
const getDrivePreviewUrl = (url) => {
  if (!url) return "";
  if (url.includes("/view")) return url.replace("/view", "/preview");
  return url;
};

export default function DayCarePage() {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      <main className="flex-1">
        {/* ====== HERO ====== */}
        <section className="relative h-[260px] md:h-[320px] lg:h-[360px] overflow-hidden">
          <Image
            src={daycareHero}
            alt="UIII Daycare"
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          <div className="relative max-w-6xl mx-auto h-full px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
            <div className="mb-4">
              <Link
                href="/business"
                className="inline-flex items-center text-sm text-white/90 hover:text-white transition"
              >
                <span className="mr-2 text-lg">←</span>
                <span>Back to Business Units</span>
              </Link>
            </div>

            <div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-white mb-3">
                UIII Daycare
              </h1>
              <p className="max-w-xl text-sm md:text-base text-slate-100">
                Our daycare service is designed to support the comfort of the
                academic community and the public by providing a safe,
                nurturing, and educational environment for children. Managed by
                trained professionals, we ensure each child receives attentive
                care and engaging activities that support their growth and
                development. Featuring a Montessori-based curriculum, our
                daycare provides a high-quality early learning experience you
                can trust.
              </p>
            </div>
          </div>
        </section>

        {/* ====== FACILITIES & EXCELLENCE ====== */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#003B5C]">
                Facilities & Excellence
              </h2>
              <p className="mt-2 text-sm md:text-base text-slate-600 max-w-xl mx-auto">
                We are committed to providing the best service for your little
                one
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-3">
              {/* Age Groups & Ratios */}
              <div className="relative rounded-2xl p-5 md:p-6 bg-gradient-to-br from-[#003B5C] via-[#004D73] to-[#00778B] text-white shadow-lg">
                <div className="absolute -top-6 -right-4 h-20 w-20 rounded-full bg-white/10 blur-2xl" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center text-xl font-semibold">
                    👶
                  </div>
                  <h3 className="text-lg font-semibold">Age Groups & Ratios</h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-100">
                  We accept children from <strong>3 months</strong> to{" "}
                  <strong>6 years old</strong>. With reasonable ratio of
                  caretaker and children:
                </p>
                <ul className="mt-3 text-sm space-y-1.5 text-slate-100/90">
                  <li>• 1:2 (&lt;2.5 y.o)</li>
                  <li>• 1:3 (2.5–4 y.o)</li>
                  <li>• 1:4 (&gt;4 y.o)</li>
                </ul>
              </div>

              {/* Montessori Philosophy */}
              <div className="relative rounded-2xl p-5 md:p-6 bg-gradient-to-br from-[#E3A130] via-[#D19020] to-[#C07810] text-white shadow-lg">
                <div className="absolute -bottom-6 -left-4 h-20 w-20 rounded-full bg-white/20 blur-2xl" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center text-xl font-semibold">
                    📚
                  </div>
                  <h3 className="text-lg font-semibold">
                    Montessori Philosophy
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-50">
                  We implement the <strong>Montessori philosophy</strong> in our
                  parenting and education programs, supported by{" "}
                  <strong>Montessori-certified teachers</strong> and a{" "}
                  <strong>Montessori-inspired environment</strong>.
                </p>
              </div>

              {/* Bilingual Environment */}
              <div className="relative rounded-2xl p-5 md:p-6 bg-gradient-to-br from-[#003B5C] via-[#005B7D] to-[#00778B] text-white shadow-lg">
                <div className="absolute -top-5 -left-4 h-20 w-20 rounded-full bg-white/15 blur-2xl" />
                <div className="flex items-center gap-3 mb-4">
                  <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center text-xl font-semibold">
                    🌍
                  </div>
                  <h3 className="text-lg font-semibold">
                    Bilingual Environment
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-slate-50">
                  Our daily languages are <strong>Bahasa Indonesia</strong> and{" "}
                  <strong>English</strong>, as some of our children are coming
                  from abroad. Creating a natural bilingual learning
                  environment.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ====== PACKAGES & PRICING ====== */}
        <section className="py-12 md:py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#003B5C]">
                Packages & Pricing
              </h2>
              <p className="mt-2 text-sm md:text-base text-slate-600 max-w-xl mx-auto">
                Choose the pricing category that applies to you
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-1">
              {/* EXTERNAL CARD (tanpa link, hanya deskripsi) */}
              <div className="group relative rounded-2xl p-6 md:p-7 bg-gradient-to-br from-[#003B5C] via-[#004D73] to-[#00778B] text-white shadow-xl overflow-hidden">
                <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,_#ffffff,_transparent_60%)]" />
                <div className="relative flex items-start gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center text-2xl">
                    🌐
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-xl font-semibold">External</h3>
                      <span className="inline-flex items-center text-xs font-medium bg-white/15 px-3 py-1 rounded-full">
                        Pricing PDF preview below
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-slate-100">
                      Pricing for external parents, non-UIII staff, and the
                      general public. Includes daily, monthly, and special
                      programs.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* PDF PREVIEW EXTERNAL */}
            <div className="mt-8">
              <h3 className="text-base md:text-lg font-semibold text-[#003B5C] mb-2">
               PDF Preview
              </h3>
              <div className="w-full h-[480px] md:h-[600px] rounded-2xl overflow-hidden border border-slate-200 bg-white shadow">
                <iframe
                  src={getDrivePreviewUrl(PRICING_DOCS.external)}
                  className="w-full h-full"
                  title="External Pricing PDF"
                />
              </div>
              <p className="mt-2 text-xs md:text-sm text-slate-500 text-center md:text-left">
                <a
                  href={PRICING_DOCS.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#00778B] hover:text-[#005b7d] font-semibold"
                >
                  click here to open it in a new tab
                </a>
                .
              </p>
            </div>

            <p className="mt-4 text-xs md:text-sm text-slate-500 text-center">
              Pricing details are subject to change. Please contact our team for
              the latest information.
            </p>
          </div>
        </section>

        {/* ====== DAILY ACTIVITY SCHEDULE ====== */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#003B5C]">
                Daily Activity Schedule
              </h2>
              <p className="mt-4 text-xs md:text-sm text-slate-500 text-center">
                Structured routine to optimize learning and children's comfort
              </p>
            </div>

            <div className="grid gap-4 md:gap-6 md:grid-cols-2">
              {[
                { time: "08.00 – 09.15", title: "Arrival" },
                { time: "09.30 – 10.00", title: "Snack Time" },
                { time: "10.00 – 10.30", title: "Circle Time" },
                {
                  time: "10.40 – 11.45",
                  title: "Fun Montessori Learning",
                  subtitle: "(Sensory stimulation inside/outside)",
                },
                { time: "11.45 – 12.40", title: "Lunch Time" },
                {
                  time: "12.40 – 13.00",
                  title: "Clean Up & Nap Preparation",
                },
                { time: "13.00 – 14.45", title: "Nap & Quiet Time" },
                { time: "15.00 – 15.30", title: "Snack Time" },
                { time: "15.30 – 16.30", title: "Clean Up & Shower Time" },
                { time: "16.30 – 17.00", title: "Pick Up Time" },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3 md:px-5 md:py-4"
                >
                  <div className="mt-1 text-xs font-semibold text-[#003B5C] whitespace-nowrap">
                    {item.time}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm md:text-base font-semibold text-slate-800">
                      {item.title}
                    </h3>
                    {item.subtitle && (
                      <p className="mt-1 text-xs md:text-sm text-slate-500">
                        {item.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== ACTIVITY GALLERY ====== */}
        <section className="py-12 md:py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col items-center gap-2 mb-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-[#003B5C]">
                Activity Gallery
              </h2>
              <p className="mt-1 text-sm md:text-base text-slate-600 max-w-xl mx-auto">
                A glimpse of our daily routines and special activities inside
                UIII Daycare.
              </p>
            </div>

            <div className="grid gap-4 md:gap-6 md:grid-cols-3">
              {[daycare1, daycare2, daycare3].map((imgSrc, idx) => (
                <div
                  key={idx}
                  className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-md bg-slate-200"
                >
                  <Image
                    src={imgSrc}
                    alt={`Daycare activity ${idx + 1}`}
                    fill
                    className="object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ====== CTA SECTION ====== */}
        <section className="py-12 md:py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl border border-slate-100 bg-gradient-to-br from-[#003B5C] via-[#004D73] to-[#00778B] text-white px-6 py-8 md:px-10 md:py-10 shadow-xl text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-3">
                Enroll Your Child Now
              </h2>
              <p className="text-sm md:text-base text-slate-100 mb-6 max-w-2xl mx-auto">
                Interested in UIII Daycare? Contact us to learn more about our
                programs, availability, and enrollment process. We are happy to
                assist parents and guardians from both inside and outside UIII.
              </p>

              <div className="flex flex-wrap gap-3 justify-center">
                <a
                  href="https://wa.me/6287749000052"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 rounded-full bg-white text-[#003B5C] text-sm font-semibold shadow hover:bg-slate-100 transition"
                >
                  📱 Contact Us via WhatsApp
                </a>
                <a
                  href="mailto:daycare@uiii.ac.id"
                  className="inline-flex items-center px-4 py-2 rounded-full border border-white/70 text-sm font-semibold text-white/95 hover:bg-white/10 transition"
                >
                  ✉️ Email Us
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
